import { useEffect } from 'react';
import { Route, useLocation } from 'wouter';

/**
 * ScrollResetRoute is a wrapper around Route that ensures scroll position
 * is reset to the top when the route is matched
 */
interface ScrollResetRouteProps {
  path: string;
  component?: React.ComponentType<any>;
  children?: React.ReactNode;
}

export function ScrollResetRoute({ path, component: Component, children }: ScrollResetRouteProps) {
  // We need to track if this route is active
  const [location] = useLocation();
  const isActive = location === path;
  
  // Reset scroll when this route becomes active
  useEffect(() => {
    if (isActive) {
      console.log(`ScrollResetRoute: Resetting scroll for path ${path}`);
      
      // Reset scroll positions
      const resetScrollPositions = () => {
        // Reset window scroll
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        
        // Also reset the main content container if it exists
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          mainContent.scrollTop = 0;
        }
        
        // Find and reset scrollable content areas, but exclude sidebar and navigation elements
        document.querySelectorAll('.overflow-y-auto, .overflow-auto').forEach(element => {
          if (element instanceof HTMLElement) {
            // Skip sidebar, navigation, and sheet content elements
            const isNavElement = element.closest('[data-sidebar]') || 
                                element.closest('[data-sheet-content]') ||
                                element.closest('.sidebar') ||
                                element.closest('nav') ||
                                element.closest('[role="navigation"]') ||
                                element.hasAttribute('data-preserve-scroll');
            
            if (!isNavElement) {
              element.scrollTop = 0;
            }
          }
        });
      };
      
      // Reset scroll immediately
      resetScrollPositions();
      
      // Reset again after layout might have settled (multiple times to ensure it works)
      // Use individual timeouts instead of an array for type safety
      const timeoutId1 = setTimeout(resetScrollPositions, 10);
      const timeoutId2 = setTimeout(resetScrollPositions, 50);
      const timeoutId3 = setTimeout(resetScrollPositions, 100);
      const timeoutId4 = setTimeout(resetScrollPositions, 250);
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        clearTimeout(timeoutId4);
      };
    }
  }, [isActive, path]);
  
  // Just render a regular Route, but with our scroll reset logic
  return (
    <Route path={path}>
      {Component ? <Component /> : children}
    </Route>
  );
}

export default ScrollResetRoute;